"use client";

import { useEffect, useRef } from "react";
import { useAuthStore } from "@/modules/auth/stores/useAuthStore";
import { useAppBoot, useAvailableStores, useResolvedStoreId } from "../hooks/useBootstrap";
import { readSelectedStoreId, useStoreSwitcher, useSwitchStore } from "@/modules/store";
import { useActiveStore } from "@/modules/store/stores/useActiveStore";

/**
 * Adoção da loja no client. Não DESCOBRE nada: a loja já foi resolvida e
 * validada no SSR (`resolve-store.ts`), a partir do cookie `selected_store`,
 * do `default_store_id` e da lista de lojas. Aqui só:
 *
 * - semeia o estado global com a loja do SSR e libera a UI (`ready`);
 * - com `can_change_store` e NENHUMA loja resolvida, abre o seletor obrigatório;
 * - quando um login nesta sessão grava um cookie novo (a loja favorita, escrita
 *   pelo BFF), adota-o e pede `router.refresh()` para o servidor re-resolver
 *   tema/settings/rodapé — sem rede extra e sem reload.
 *
 * Nada de rede neste arquivo: antes ele buscava o perfil em TODO carregamento
 * autenticado e recarregava a página quando a favorita divergia. A 15 mil
 * acessos simultâneos isso era uma chamada privada (sem cache) por page view e,
 * para quem entrava num aparelho novo, dois SSRs. Hoje a favorita vira cookie
 * no login (`api/auth/login`) e chega aqui já resolvida.
 */
export function StoreInit() {
    const resolvedStoreId = useResolvedStoreId();
    const stores = useAvailableStores();
    const canChange = useAppBoot()?.can_change_store ?? false;
    const authStatus = useAuthStore((s) => s.status);
    const switchStore = useSwitchStore();

    // Último cookie que este componente mandou o servidor re-resolver. Cada
    // valor é adotado UMA vez: se por alguma razão o servidor não o aceitar, o
    // efeito segue com a loja resolvida em vez de entrar num laço de refresh.
    const adoptedCookieRef = useRef<number | null>(null);

    useEffect(() => {
        // `authStatus` entra nas dependências de propósito: o login grava o
        // cookie da favorita e este efeito precisa rodar de novo para adotá-la.
        if (authStatus === "loading") return;

        const active = useActiveStore.getState();

        // Cookie recém-gravado (login) que o SSR desta página ainda não viu:
        // adota e re-resolve no servidor. Só lojas conhecidas, pelo mesmo
        // critério do servidor — evita um refresh que não mudaria nada.
        const cookieStoreId = canChange ? readSelectedStoreId() : null;
        const cookieIsKnown =
            cookieStoreId !== null && stores.some((store) => store.store_id === cookieStoreId);
        if (
            cookieStoreId !== null &&
            cookieIsKnown &&
            cookieStoreId !== resolvedStoreId &&
            adoptedCookieRef.current !== cookieStoreId
        ) {
            adoptedCookieRef.current = cookieStoreId;
            switchStore(cookieStoreId);
            return;
        }

        active.setStoreId(resolvedStoreId);
        active.setReady(true);

        // Sem loja resolvida e com lojas para escolher: seleção obrigatória.
        // Troca proibida sem loja é má configuração — logada no servidor.
        if (canChange && resolvedStoreId <= 0 && stores.length > 0) {
            useStoreSwitcher.getState().openModal({ required: true });
        }
    }, [authStatus, canChange, resolvedStoreId, stores, switchStore]);

    return null;
}
